import { Link } from 'react-router-dom'
import { Button, Container, Heading, Text } from '.'

export function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-slate-300 pt-24">
      {/* Subtle decorative elements */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl" />

      <Container className="relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 border border-primary-300">
              <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
              <span className="text-sm text-primary-800 font-medium">
                App monitoring made simple
              </span>
            </div>

            <div className="space-y-6">
              <Heading as="h1" className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="text-slate-900">Monitor errors</span>
                <br />
                <span className="bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">
                  before users notice
                </span>
              </Heading>

              <Text size="lg" className="text-slate-600 max-w-xl text-xl">
                Error tracking, uptime monitoring, and custom metrics that just work. No enterprise
                complexity, just track errors and get alerts.
              </Text>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/signup">
                <Button variant="primary" size="lg" className="group">
                  Start Free
                  <svg
                    className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="lg">
                  Sign In
                </Button>
              </Link>
            </div>

            {/* Quick stats */}
            <div className="flex gap-8 pt-8 border-t border-slate-200">
              <div>
                <div className="text-2xl font-bold text-slate-900">5 min</div>
                <div className="text-sm text-slate-500">Setup time</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">$20/mo</div>
                <div className="text-sm text-slate-500">Starter plan</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">100%</div>
                <div className="text-sm text-slate-500">Free forever tier</div>
              </div>
            </div>
          </div>

          {/* Right: Visual/Code Snippet */}
          <div className="lg:pl-12">
            <div className="relative">
              {/* Code snippet mockup - keeping dark for contrast/readability */}
              <div className="relative rounded-2xl bg-slate-50 border border-slate-300 shadow-2xl overflow-hidden">
                {/* Terminal header */}
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-200 border-b border-slate-300">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className="text-xs text-slate-400 ml-4 font-mono">app-status.json</div>
                </div>

                {/* Code content */}
                <div className="p-6 font-mono text-sm space-y-3">
                  <div className="text-slate-500">{'{'}</div>
                  <div className="pl-4 text-slate-400">
                    <span className="text-blue-400">"app"</span>:
                    <span className="text-green-400"> "my-app"</span>,
                  </div>
                  <div className="pl-4 text-slate-400">
                    <span className="text-blue-400">"status"</span>:
                    <span className="text-green-400"> "healthy"</span>,
                  </div>
                  <div className="pl-4 text-slate-400">
                    <span className="text-blue-400">"uptime_24h"</span>:
                    <span className="text-purple-400"> 99.9</span>%,
                  </div>

                  {/* Error stats */}
                  <div className="pl-4">
                    <span className="text-blue-400">"errors_24h"</span>:
                    <span className="text-purple-400"> 3</span>,
                  </div>

                  <div className="pl-4">
                    <span className="text-blue-400">"latest_error"</span>: {'{'}
                  </div>

                  <div className="pl-8 bg-red-500/10 border-l-2 border-red-500 py-2 -ml-2 pl-10">
                    <div className="text-red-400">
                      <span className="text-blue-400">"type"</span>:
                      <span className="text-red-300"> "TypeError"</span>,
                    </div>
                    <div className="text-red-400">
                      <span className="text-blue-400">"message"</span>:
                      <span className="text-red-300"> "Cannot read null"</span>
                    </div>
                  </div>

                  <div className="pl-4 text-slate-500">{'}'}</div>
                  <div className="text-slate-500">{'}'}</div>

                  {/* Alert notification */}
                  <div className="mt-4 flex items-center gap-3 bg-primary-500/20 border border-primary-500/40 rounded-lg p-3">
                    <svg
                      className="w-5 h-5 text-primary-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    <span className="text-sm text-primary-300">Alert sent to team@example.com</span>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg rotate-12">
                Instant results
              </div>
            </div>
          </div>
        </div>
      </Container>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <svg
          className="w-6 h-6 text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </div>
    </section>
  )
}
